// Admin → Users keeps its whole view in the address bar: search, filters,
// sort, page and which rows are open. Leaving for a device and coming back
// (browser Back, or the device page's own Back) then lands on the same view
// instead of a fresh, unfiltered page 1.
//
// The links other pages already make still read the same way:
// ?search=<email>&expand=<userId> (hardware orders) and ?package=<id> (packages).

export const USERS_ORIGINS = ['vortex', 'passagewiki'] as const;
export const USERS_ROLES = ['user', 'admin', 'super_admin', 'tester', 'tester_plus'] as const;
export const USERS_STATUSES = ['active', 'suspended'] as const;
/** Is this the plan they pay for? Same names as the API's (vortex-api lib/planBilling.ts BILLING_FILTERS). */
export const USERS_BILLING = [
  'needs_look', 'mismatch', 'not_billed_regular', 'unknown', 'payment_failing', 'stripe_ended',
  'overridden', 'not_billed_tester', 'paid', 'no_plan',
] as const;
export const USERS_SORT_KEYS = ['name', 'subscription', 'expiry', 'status', 'joined', 'deviceCount', 'lastSignIn', 'deviceLastSeen'] as const;

export interface UsersListState {
  search: string;
  origin: string;
  role: string;
  status: string;
  packageId: string;
  /** One of USERS_BILLING, or '' for everyone. */
  billing: string;
  /** Column key, or '' for the server's order. */
  sort: string;
  dir: 'asc' | 'desc';
  /** 1-based. */
  page: number;
  /** User ids whose rows are open, in the order they were opened. */
  expanded: string[];
}

export const USERS_LIST_DEFAULTS: UsersListState = {
  search: '',
  origin: '',
  role: '',
  status: '',
  packageId: '',
  billing: '',
  sort: '',
  dir: 'asc',
  page: 1,
  expanded: [],
};

function oneOf(value: string | null, allowed: readonly string[]): string {
  return value && allowed.includes(value) ? value : '';
}

/** Reads the view from the address; anything unusable falls back to its default. */
export function readUsersListState(params: URLSearchParams): UsersListState {
  const page = Number(params.get('page'));
  const sort = oneOf(params.get('sort'), USERS_SORT_KEYS);
  const expanded = [...new Set(
    params.getAll('expand').flatMap((v) => v.split(',')).map((v) => v.trim()).filter(Boolean),
  )];
  return {
    search: params.get('search') ?? '',
    origin: oneOf(params.get('origin'), USERS_ORIGINS),
    role: oneOf(params.get('role'), USERS_ROLES),
    status: oneOf(params.get('status'), USERS_STATUSES),
    packageId: params.get('package') ?? '',
    billing: oneOf(params.get('billing'), USERS_BILLING),
    sort,
    dir: sort && params.get('dir') === 'desc' ? 'desc' : 'asc',
    page: Number.isInteger(page) && page > 1 ? page : 1,
    expanded,
  };
}

/** Writes the view back, leaving out whatever is at its default. */
export function writeUsersListState(state: UsersListState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.search) params.set('search', state.search);
  if (state.origin) params.set('origin', state.origin);
  if (state.role) params.set('role', state.role);
  if (state.status) params.set('status', state.status);
  if (state.packageId) params.set('package', state.packageId);
  if (state.billing) params.set('billing', state.billing);
  if (state.sort) {
    params.set('sort', state.sort);
    if (state.dir === 'desc') params.set('dir', 'desc');
  }
  if (state.page > 1) params.set('page', String(state.page));
  if (state.expanded.length) params.set('expand', state.expanded.join(','));
  return params;
}
